import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { isArray, isEmpty, isEqual, isNil, keyBy, sortBy } from 'lodash'
import { v4 as uuid } from 'uuid'
import { IconDeviceFloppy, IconFilePlus, IconPencil, IconSparkles } from '@tabler/icons-react'

import { BasicModal } from '@/components/layout/modals/BasicModal'
import { Loader } from '@/components/loaders/Loader'
import { Button } from '@/components/buttons/Button'
import { SegmentedControl } from '@/components/buttons/SegmentedControl'
import { Card } from '@/components/cards/Card'
import { Table } from '@/components/tables/Table/Table'
import Select from '@/components/forms/Select'
import { ErrorMessage } from '@/components/forms/ErrorMessage'
import { Pill } from '@/components/pills/Pill'
import { FormField } from '@/components/forms/FormField'
import { TextInput } from '@/components/forms/TextInput'
import { toast } from '@/components/toasts/toast.helpers'
import { closeModal } from '@/reducers/ui/ui.redux'
import { fetchOrganizations } from '@/reducers/organizations/organizations.actions'
import {
  createScorecardConfig,
  fetchAgentsByOrg,
  fetchOrganizationsMappedVoipCampaigns,
  fetchPlaybooksByOrg,
  fetchScorecardConfigById,
  fetchTagsByOrg,
  updateScorecardConfig,
} from '@/reducers/qa-copilot/qa-copilot.actions'
import { fetchModels, generateCopilotPrompt } from '@/reducers/prompts/prompts.actions'
import { fetchingAPI } from '@/api/fetchingAPI'
import { apiErrorMessage } from '@/api/handleResponse'
import { apiService } from '@/api/apiService'
import { getOrganizationOptions } from '@/utils/helpers'

import { getDefaultFallbackModelId, getDefaultQaCopilotModelId } from '../QACopilot.helpers'
import { clearGuidedDraft, loadGuidedDraft, saveGuidedDraft } from './guidedDraft.helpers'
import {
  GuidedEligibilityStep,
  GuidedStepHeader,
  GuidedReviewStructure,
  GuidedStepNav,
  GuidedStructureStep,
  isCriteriaRowComplete,
  keepsExistingPrompt,
  parsePositiveInteger,
  pluralize,
} from '../components/QACopilotGuidedBuilder'

import '../QACopilot.scss'

const TRANSCRIPT_BLOCK_REGEX = /#START OF TRANSCRIPT[\s\S]*?#END OF TRANSCRIPT/g
const DEFAULT_GUIDED_POINTS = 1
const INCOMPLETE_NAMES_SHOWN = 3

const GUIDED_STEPS = {
  welcome: 'welcome',
  eligibility: 'eligibility',
  custom: 'custom',
  review: 'review',
}

const STEP_DEFINITIONS = [
  {
    key: GUIDED_STEPS.welcome,
    title: 'Basics',
    subtitle: 'Name and organization',
    description: 'Name this scorecard and choose who it belongs to.',
  },
  {
    key: GUIDED_STEPS.eligibility,
    title: 'Eligibility',
    subtitle: 'What gets graded',
    description: 'Describe which calls get reviewed. Nothing here is required.',
  },
  {
    key: GUIDED_STEPS.custom,
    title: 'Structure',
    subtitle: 'Sections and criteria',
    description: 'Switch between sections in the tab strip, then open a criterion to edit it',
  },
  {
    key: GUIDED_STEPS.review,
    title: 'Review',
    subtitle: 'Confirm and create',
    description: 'Check the setup, then create the scorecard.',
  },
]

const STEP_ORDER = STEP_DEFINITIONS.map((step) => step.key)

const EDIT_STEP_COPY = {
  [GUIDED_STEPS.welcome]: {
    description:
      'Pick the scorecard to change. Its filters, sections and criteria load into the next steps',
  },
  [GUIDED_STEPS.eligibility]: {
    description: 'The current rules are kept unless you open them and make a change',
  },
  [GUIDED_STEPS.review]: {
    subtitle: 'Confirm and save',
    description: 'Check the changes, then save the scorecard',
  },
}

const BUILDER_MODES = [
  { key: 'create', label: 'Create new scorecard', icon: <IconFilePlus /> },
  { key: 'edit', label: 'Edit existing scorecard', icon: <IconPencil /> },
]

const INITIAL_ELIGIBILITY = {
  reviewRules: '',
  exclusions: '',
  minDurationMinutes: 1,
  maxDurationMinutes: '',
  agents: [],
  tags: [],
  playbooks: [],
  voipCampaigns: [],
  usersInclusive: 'true',
  tagsInclusive: 'true',
  voipCampaignsInclusive: 'true',
}

const ORG_SCOPED_ELIGIBILITY_FIELDS = {
  agents: [],
  tags: [],
  playbooks: [],
  voipCampaigns: [],
}

const createCustomCriteriaRow = (sectionId) => ({
  rowId: uuid(),
  sectionId,
  criterionName: '',
  passScenario: '',
  failScenario: '',
  notApplicableScenario: '',
  points: DEFAULT_GUIDED_POINTS,
})

const createSection = (name) => ({ id: uuid(), name })

const ensureTranscriptPlaceholder = (prompt = '') =>
  `#START OF TRANSCRIPT\n$transcript\n#END OF TRANSCRIPT\n\n${prompt.replace(TRANSCRIPT_BLOCK_REGEX, '').trim()}`

const normalizeSectionName = (name = '') => name.trim().toLowerCase()

const ELIGIBILITY_FILTER_FIELDS = [
  [
    'minDurationMinutes',
    (value) => ({ min_duration_in_seconds: parsePositiveInteger(value, 1) * 60 }),
  ],
  [
    'maxDurationMinutes',
    (value) => {
      const minutes = parsePositiveInteger(value, null)
      return { max_duration_in_seconds: minutes ? minutes * 60 : undefined }
    },
  ],
  ['agents', (value) => ({ users: value.map((agent) => ({ id: agent.value })) })],
  ['usersInclusive', (value) => ({ users_inclusive: value !== 'false' })],
  ['tags', (value) => ({ tags: value.map((tag) => ({ id: tag.value })) })],
  ['tagsInclusive', (value) => ({ tags_inclusive: value !== 'false' })],
  ['playbooks', (value) => ({ config_cids: value.map((playbook) => playbook.value) })],
  [
    'voipCampaigns',
    (value) => ({ voip_campaigns: value.map((campaign) => ({ id: campaign.value })) }),
  ],
  ['voipCampaignsInclusive', (value) => ({ voip_campaigns_inclusive: value !== 'false' })],
]

const filterFingerprint = (value) =>
  isArray(value) ? value.map((option) => option.value) : `${value}`

const eligibilityFilterOverrides = (eligibility, loadedEligibility) =>
  Object.assign(
    {},
    ...ELIGIBILITY_FILTER_FIELDS.filter(
      ([key]) =>
        isNil(loadedEligibility) ||
        !isEqual(filterFingerprint(eligibility[key]), filterFingerprint(loadedEligibility[key]))
    ).map(([key, toApi]) => toApi(eligibility[key]))
  )

const FILTER_LABELS = {
  minDurationMinutes: 'Minimum call length',
  maxDurationMinutes: 'Maximum call length',
  agents: 'Agents',
  usersInclusive: 'Agents rule',
  tags: 'Tags',
  tagsInclusive: 'Tags rule',
  playbooks: 'Playbooks',
  voipCampaigns: 'VoIP campaigns',
  voipCampaignsInclusive: 'VoIP campaigns rule',
}

const describeFilterValue = (key, value) => {
  if (isArray(value)) {
    return isEmpty(value) ? 'none' : value.map((option) => option.label).join(', ')
  }
  if (key.endsWith('Inclusive')) {
    return value === 'false' ? 'Excludes' : 'Includes'
  }
  return isEmpty(`${value}`) ? 'none' : `${value} min`
}

const describeChanges = (current, loaded) => {
  const changes = []
  const push = (kind, where, item, detail) => changes.push({ kind, where, item, detail })

  if (current.eligibility.reviewRules.trim() || current.eligibility.exclusions.trim()) {
    push('changed', 'Eligibility', 'Rules', 'Rewritten from the new answers')
  }
  ELIGIBILITY_FILTER_FIELDS.forEach(([key]) => {
    const before = loaded.eligibility[key]
    const after = current.eligibility[key]
    if (!isEqual(filterFingerprint(before), filterFingerprint(after))) {
      push(
        'changed',
        'Eligibility',
        FILTER_LABELS[key],
        `${describeFilterValue(key, before)} → ${describeFilterValue(key, after)}`
      )
    }
  })

  const loadedSections = keyBy(loaded.sections, 'id')
  const currentSections = keyBy(current.sections, 'id')
  current.sections.forEach((section) => {
    const before = loadedSections[section.id]
    if (isNil(before)) {
      push('added', 'Structure', `Section “${section.name}”`, 'New section')
    } else if (before.name !== section.name) {
      push('changed', 'Structure', `Section “${section.name}”`, `Renamed from “${before.name}”`)
    }
  })
  loaded.sections
    .filter((section) => isNil(currentSections[section.id]))
    .forEach((section) =>
      push('removed', 'Structure', `Section “${section.name}”`, 'Deleted with its criteria')
    )

  const sectionName = (id) => currentSections[id]?.name ?? loadedSections[id]?.name ?? ''
  const rowName = (row) => row.criterionName.trim() || 'Untitled Criterion'
  const loadedRows = keyBy(loaded.criteriaRows, 'rowId')
  const currentRows = keyBy(current.criteriaRows, 'rowId')
  current.criteriaRows.forEach((row) => {
    const before = loadedRows[row.rowId]
    const where = sectionName(row.sectionId)
    if (isNil(before)) {
      push('added', where, rowName(row), `New criterion, ${row.points} pts`)
      return
    }
    if (before.criterionName !== row.criterionName) {
      push('changed', where, rowName(row), `Renamed from “${before.criterionName}”`)
    }
    if (`${before.points}` !== `${row.points}`) {
      push('changed', where, rowName(row), `${before.points} pts → ${row.points} pts`)
    }
    if (before.sectionId !== row.sectionId) {
      push('changed', where, rowName(row), `Moved from “${sectionName(before.sectionId)}”`)
    }
    if (!keepsExistingPrompt(row)) {
      push('changed', where, rowName(row), 'Scoring rules rewritten')
    }
  })
  loaded.criteriaRows
    .filter((row) => isNil(currentRows[row.rowId]))
    .forEach((row) => push('removed', sectionName(row.sectionId), rowName(row), 'Deleted'))

  return changes
}

const buildGuidedSectionsAndPrompts = async ({
  sections,
  criteriaRows,
  existingSections = [],
  existingPromptsByScid = {},
  generatePrompt,
  defaultQaCopilotModelId,
}) => {
  const existingSectionsBySsid = keyBy(existingSections, 'ssid')
  const existingCriteriaByScid = keyBy(
    existingSections.flatMap((section) => section.measures.flatMap((measure) => measure.criteria)),
    'scid'
  )
  const builtSections = []
  const promptsToAdd = []

  for (const section of sections) {
    const sectionRows = criteriaRows.filter((row) => row.sectionId === section.id)
    const existingSection = existingSectionsBySsid[section.id]

    const criteria = []

    for (const row of sectionRows) {
      const existingCriterion = existingCriteriaByScid[row.scid]
      const weight = parsePositiveInteger(row.points, DEFAULT_GUIDED_POINTS)

      if (keepsExistingPrompt(row)) {
        criteria.push({ ...existingCriterion, name: row.criterionName, weight, ssid: section.id })
      } else {
        const scid = row.scid || uuid()
        const description = row.passScenario || row.criterionName
        const prompt = await generatePrompt({
          description,
          passScenario: row.passScenario,
          failScenario: row.failScenario,
          notApplicableScenario: row.notApplicableScenario,
        })

        criteria.push({
          ...existingCriterion,
          scid,
          name: row.criterionName,
          description,
          criteria_type: 'ai',
          must_be_present: true,
          side: 'both',
          max_response_tokens: 256,
          weight,
          ssid: section.id,
        })
        promptsToAdd.push({
          ...existingPromptsByScid[scid],
          scid,
          user_prompt: prompt,
          side: 'both',
          llm_model_id: existingPromptsByScid[scid]?.llm_model_id ?? defaultQaCopilotModelId,
          feature: 'qa_copilot',
        })
      }
    }

    const existingMeasure = existingSection?.measures?.[0]
    builtSections.push({
      ...existingSection,
      ssid: section.id,
      name: section.name,
      order_id: builtSections.length,
      measures: [
        {
          ...existingMeasure,
          name: existingMeasure?.name ?? `${section.name} Measure`,
          criteria,
        },
      ],
    })
  }

  return { sections: builtSections, promptsToAdd }
}

const GuidedCreateFlow = ({
  organizationOptions,
  currentUserOrganizationId,
  currentUserId,
  eligibilityOptions = {},
  eligibilityLoading = {},
  isEditing = false,
  initialOrganizationId = '',
  scorecardOptions = [],
  scorecardOptionsLoading = false,
  onModeChange = () => {},
  onOrganizationChange = () => {},
  onLoadScorecard,
  onCreateScorecard,
  onUpdateScorecard,
  onClose,
  modalTitle,
}) => {
  const isBaltoAdmin = currentUserOrganizationId === 1
  // Read once per mount: the draft is this user's previously autosaved wizard state.
  const [draft] = useState(() => {
    const savedDraft = loadGuidedDraft(currentUserId)
    if ((savedDraft?.mode ?? 'create') !== (isEditing ? 'edit' : 'create')) {
      return null
    }
    const draftOrganizationId =
      initialOrganizationId ||
      (isBaltoAdmin ? savedDraft?.organizationId : currentUserOrganizationId) ||
      ''
    if (isEditing && `${savedDraft?.organizationId ?? ''}` !== `${draftOrganizationId}`) {
      return null
    }
    return savedDraft
  })
  const loadRequestRef = useRef(0)
  // Only Balto admins get an organization picker, so only their draft may carry an organization:
  // replaying a saved one for anyone else would silently pin the scorecard to an org they can
  // neither see nor change (their own org may have changed since the draft was saved).
  const defaultOrganizationId = isBaltoAdmin ? '' : currentUserOrganizationId || ''
  const restoredOrganizationId =
    initialOrganizationId || (isBaltoAdmin && draft?.organizationId) || defaultOrganizationId
  // Agents, tags, playbooks and campaigns are ids scoped to the org they were picked under. When a
  // draft comes back under a different org — the user moved orgs since saving, so the guard above
  // pinned them to their new one — those ids belong to an org this scorecard is not for. Changing
  // the org in the picker already clears them; restoring has to do the same, or they ride along
  // invisibly into the created config.
  const isDraftOrganizationCurrent =
    isNil(draft) || `${draft.organizationId ?? ''}` === `${restoredOrganizationId}`

  const [currentStep, setCurrentStep] = useState(
    draft?.currentStep && STEP_ORDER.includes(draft.currentStep)
      ? draft.currentStep
      : GUIDED_STEPS.welcome
  )
  const [scorecardName, setScorecardName] = useState(draft?.scorecardName || '')
  const [organizationId, setOrganizationId] = useState(restoredOrganizationId)

  useEffect(() => {
    if (restoredOrganizationId) {
      onOrganizationChange(restoredOrganizationId)
    }
  }, [])
  const [eligibility, setEligibility] = useState({
    ...INITIAL_ELIGIBILITY,
    ...draft?.eligibility,
    ...(isDraftOrganizationCurrent ? {} : ORG_SCOPED_ELIGIBILITY_FIELDS),
  })
  const [sections, setSections] = useState(draft?.sections || [])
  const [criteriaRows, setCriteriaRows] = useState(draft?.criteriaRows || [])
  const [selectedCriteriaRowId, setSelectedCriteriaRowId] = useState(
    draft?.criteriaRows[0]?.rowId || null
  )
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false)
  const [validationError, setValidationError] = useState('')
  const [submitError, setSubmitError] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [existingConfig, setExistingConfig] = useState(null)
  const [isLoadingScorecard, setIsLoadingScorecard] = useState(false)
  const [loadedState, setLoadedState] = useState(draft?.loadedState ?? null)

  useEffect(() => {
    if (isEditing && draft?.scorecardConfigId) {
      const requestId = ++loadRequestRef.current
      onLoadScorecard(draft.scorecardConfigId).then((config) => {
        if (!isNil(config) && requestId === loadRequestRef.current) {
          setExistingConfig(config)
        }
      })
    }
  }, [])

  const stepDefinitions = STEP_DEFINITIONS.map((step) => ({
    ...step,
    ...(isEditing ? EDIT_STEP_COPY[step.key] : {}),
  }))

  const updateEligibility = (patch) => setEligibility((prev) => ({ ...prev, ...patch }))

  const clearLoadedScorecard = () => {
    loadRequestRef.current += 1
    setExistingConfig(null)
    setLoadedState(null)
    setScorecardName('')
    setEligibility(INITIAL_ELIGIBILITY)
    setSections([])
    setCriteriaRows([])
    setSelectedCriteriaRowId(null)
  }

  const labelFor = (options, value) =>
    options.find((option) => option.value === value)?.label ?? `${value}`

  const applyExistingConfig = (config) => {
    const tagOptions = (eligibilityOptions.tags || []).flatMap((group) => group.options || [])
    const configSections = config.sections || []

    setExistingConfig(config)
    setScorecardName(config.name || '')
    const eligibilityFromConfig = {
      ...INITIAL_ELIGIBILITY,
      minDurationMinutes: `${Math.round((config.min_duration_in_seconds || 60) / 60) || 1}`,
      maxDurationMinutes: config.max_duration_in_seconds
        ? Math.round(config.max_duration_in_seconds / 60)
        : '',
      agents: (config.users || []).map(({ id }) => ({
        value: id,
        label: labelFor(eligibilityOptions.agents || [], id),
      })),
      tags: (config.tags || []).map(({ id }) => ({ value: id, label: labelFor(tagOptions, id) })),
      playbooks: (config.config_cids || []).map((cid) => ({
        value: cid,
        label: labelFor(eligibilityOptions.playbooks || [], cid),
      })),
      voipCampaigns: (config.voip_campaigns || []).map(({ id }) => ({
        value: id,
        label: labelFor(eligibilityOptions.voipCampaigns || [], id),
      })),
      usersInclusive: `${config.users_inclusive ?? true}`,
      tagsInclusive: `${config.tags_inclusive ?? true}`,
      voipCampaignsInclusive: `${config.voip_campaigns_inclusive ?? true}`,
    }
    const stateFromConfig = {
      scorecardName: config.name || '',
      eligibility: eligibilityFromConfig,
      sections: configSections.map((section) => ({ id: section.ssid, name: section.name })),
      criteriaRows: configSections.flatMap((section) =>
        section.measures
          .flatMap((measure) => measure.criteria)
          .map((criterion) => ({
            rowId: criterion.scid,
            sectionId: section.ssid,
            criterionName: criterion.name || '',
            passScenario: '',
            failScenario: '',
            notApplicableScenario: '',
            points: `${criterion.weight ?? DEFAULT_GUIDED_POINTS}`,
            scid: criterion.scid,
            promptId: criterion.prompt_id,
          }))
      ),
    }
    setEligibility(stateFromConfig.eligibility)
    setSections(stateFromConfig.sections)
    setCriteriaRows(stateFromConfig.criteriaRows)
    setLoadedState(stateFromConfig)
    setSelectedCriteriaRowId(null)
    setValidationError('')
  }

  const handleSelectScorecard = async (option) => {
    if (isNil(option)) {
      clearLoadedScorecard()
      return
    }
    const requestId = ++loadRequestRef.current
    setIsLoadingScorecard(true)
    const config = await onLoadScorecard(option.value)
    if (requestId !== loadRequestRef.current) {
      return
    }
    setIsLoadingScorecard(false)
    if (isNil(config)) {
      setValidationError('Unable to load that scorecard. Please try again')
      return
    }
    applyExistingConfig(config)
  }

  const hasDuplicateSectionName = (sectionName, ignoredSectionId = null) => {
    const normalizedSectionName = normalizeSectionName(sectionName)

    return sections.some(
      (section) =>
        section.id !== ignoredSectionId &&
        normalizeSectionName(section.name) === normalizedSectionName
    )
  }
  const hasDuplicateSectionNames =
    new Set(sections.map((section) => normalizeSectionName(section.name))).size !== sections.length

  const isWelcomeStepValid =
    scorecardName.trim() !== '' &&
    `${organizationId}`.trim() !== '' &&
    (!isEditing || !isNil(existingConfig))
  const completeCriteriaRowsCount = criteriaRows.filter(isCriteriaRowComplete).length
  const isCustomStepValid =
    !isEmpty(sections) &&
    !isEmpty(criteriaRows) &&
    completeCriteriaRowsCount === criteriaRows.length &&
    !hasDuplicateSectionNames

  // A disabled Next with no explanation is a dead end, and the criteria holding it up can be
  // buried in any collapsed section. Name them rather than just saying something is wrong.
  const structureBlockedReason = () => {
    if (isEmpty(sections)) {
      return 'Add a section before continuing.'
    }
    if (isEmpty(criteriaRows)) {
      return 'Add at least one criterion before continuing.'
    }
    if (hasDuplicateSectionNames) {
      return 'Section names must be unique.'
    }

    const incompleteNames = criteriaRows
      .filter((criteriaRow) => !isCriteriaRowComplete(criteriaRow))
      .map((criteriaRow) => criteriaRow.criterionName.trim() || 'Untitled Criterion')

    if (isEmpty(incompleteNames)) {
      return ''
    }

    const remainingCount = incompleteNames.length - INCOMPLETE_NAMES_SHOWN
    const namesList = incompleteNames.slice(0, INCOMPLETE_NAMES_SHOWN).join(', ')

    return `Fix errors in: ${namesList}${remainingCount > 0 ? ` and ${remainingCount} more` : ''}`
  }
  const currentState = { scorecardName, eligibility, sections, criteriaRows }
  const hasUnsavedProgress = isEditing
    ? !isNil(loadedState) && !isEqual(currentState, loadedState)
    : scorecardName.trim() !== '' ||
      (isBaltoAdmin && `${organizationId}`.trim() !== '') ||
      eligibility.reviewRules.trim() !== '' ||
      eligibility.exclusions.trim() !== '' ||
      eligibility.minDurationMinutes !== INITIAL_ELIGIBILITY.minDurationMinutes ||
      `${eligibility.maxDurationMinutes}` !== `${INITIAL_ELIGIBILITY.maxDurationMinutes}` ||
      !isEmpty(eligibility.agents) ||
      !isEmpty(eligibility.tags) ||
      !isEmpty(eligibility.playbooks) ||
      !isEmpty(eligibility.voipCampaigns) ||
      eligibility.usersInclusive !== INITIAL_ELIGIBILITY.usersInclusive ||
      eligibility.tagsInclusive !== INITIAL_ELIGIBILITY.tagsInclusive ||
      eligibility.voipCampaignsInclusive !== INITIAL_ELIGIBILITY.voipCampaignsInclusive ||
      !isEmpty(sections) ||
      !isEmpty(criteriaRows)
  const isReviewReadyForSubmit =
    isWelcomeStepValid && isCustomStepValid && (!isEditing || hasUnsavedProgress)

  const draftBody = {
    mode: isEditing ? 'edit' : 'create',
    scorecardConfigId: existingConfig?.id ?? draft?.scorecardConfigId ?? null,
    loadedState,
    currentStep,
    organizationId,
    ...currentState,
  }

  const handleMoveToEligibilityStep = () => {
    if (!isEditing && !scorecardName.trim()) {
      setValidationError('Please enter a scorecard name.')
      return
    }
    if (!`${organizationId}`.trim()) {
      setValidationError('Please choose an organization.')
      return
    }
    if (isEditing && isNil(existingConfig)) {
      setValidationError('Please choose a scorecard to edit')
      return
    }
    setValidationError('')
    setCurrentStep(GUIDED_STEPS.eligibility)
  }

  const handleMoveToCustomStep = () => {
    setValidationError('')
    setCurrentStep(GUIDED_STEPS.custom)
  }

  const handleAddSection = (name) => {
    const trimmedSectionName = name.trim()

    if (hasDuplicateSectionName(trimmedSectionName)) {
      setValidationError('Section names must be unique.')
      return false
    }

    setSections([...sections, createSection(trimmedSectionName)])
    setValidationError('')
    return true
  }

  const handleUpdateSectionName = (sectionId, name) => {
    const trimmed = name.trim()
    if (!trimmed) {
      return false
    }

    if (hasDuplicateSectionName(trimmed, sectionId)) {
      setValidationError('Section names must be unique.')
      return false
    }

    setSections(
      sections.map((section) =>
        section.id === sectionId ? { ...section, name: trimmed } : section
      )
    )
    setValidationError('')
    return true
  }

  const handleRemoveSection = (sectionToRemove) => {
    const sectionId = sectionToRemove.id
    const remainingRows = criteriaRows.filter((criteriaRow) => criteriaRow.sectionId !== sectionId)

    setSections(sections.filter((section) => section.id !== sectionId))
    setCriteriaRows(remainingRows)

    if (!remainingRows.find((criteriaRow) => criteriaRow.rowId === selectedCriteriaRowId)) {
      setSelectedCriteriaRowId(remainingRows[0]?.rowId || null)
    }
  }

  const handleAddCriterionToSection = (sectionId) => {
    const newCustomCriteriaRow = createCustomCriteriaRow(sectionId)
    setCriteriaRows([...criteriaRows, newCustomCriteriaRow])
    setSelectedCriteriaRowId(newCustomCriteriaRow.rowId)
    setValidationError('')
  }

  const handleReplaceCriterion = (replacementRow) => {
    setCriteriaRows(
      criteriaRows.map((criteriaRow) =>
        criteriaRow.rowId === replacementRow.rowId ? replacementRow : criteriaRow
      )
    )
  }

  const handleUpdateSelectedCriteria = (fieldName, fieldValue) => {
    setCriteriaRows(
      criteriaRows.map((criteriaRow) =>
        criteriaRow.rowId === selectedCriteriaRowId
          ? { ...criteriaRow, [fieldName]: fieldValue }
          : criteriaRow
      )
    )
  }

  const handleRemoveCriterion = (criteriaIdToRemove) => {
    const rowsAfterRemoval = criteriaRows.filter(
      (criteriaRow) => criteriaRow.rowId !== criteriaIdToRemove
    )
    setCriteriaRows(rowsAfterRemoval)
    if (selectedCriteriaRowId === criteriaIdToRemove) {
      setSelectedCriteriaRowId(rowsAfterRemoval[0]?.rowId || null)
    }
  }

  const handleMoveToReviewStep = () => {
    if (isEmpty(sections) || isEmpty(criteriaRows)) {
      setValidationError('Add at least one section with a complete criterion before continuing.')
      return
    }
    if (hasDuplicateSectionNames) {
      setValidationError('Section names must be unique before reviewing.')
      return
    }
    if (completeCriteriaRowsCount !== criteriaRows.length) {
      setValidationError('Please complete all required criterion fields before reviewing.')
      return
    }
    setValidationError('')
    setSubmitError('')
    setCurrentStep(GUIDED_STEPS.review)
  }

  const handleSubmitGuidedSetup = async () => {
    setSubmitError('')
    setValidationError('')
    setIsSubmitting(true)

    try {
      const payload = {
        scorecardName,
        organizationId,
        eligibilityReviewRules: eligibility.reviewRules,
        eligibilityExclusions: eligibility.exclusions,
        eligibility,
        loadedEligibility: loadedState?.eligibility,
        sections,
        criteriaRows,
        existingConfig,
      }

      if (isEditing) {
        await onUpdateScorecard(payload)
      } else {
        await onCreateScorecard(payload)
      }
      clearGuidedDraft(currentUserId)
    } catch (error) {
      setSubmitError(
        error.message ||
          (isEditing
            ? 'Unable to save changes. Please review and retry'
            : 'Unable to create scorecard. Please review and retry')
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleSaveDraft = () => {
    if (saveGuidedDraft(currentUserId, draftBody)) {
      toast.success('Saved as draft')
    } else {
      toast.error('Could not save your draft \u2014 this browser is blocking local storage.')
    }
  }

  const handleRequestClose = () => {
    if (isSubmitting) {
      return
    }

    if (hasUnsavedProgress) {
      setShowUnsavedChangesModal(true)
      return
    }

    onClose()
  }

  const handleDiscardChanges = () => {
    clearGuidedDraft(currentUserId)
    setShowUnsavedChangesModal(false)
    onClose()
  }

  const handleSaveDraftAndClose = () => {
    handleSaveDraft()
    setShowUnsavedChangesModal(false)
    onClose()
  }

  const draftButtons = !isSubmitting && hasUnsavedProgress && (
    <Button data-testid="guided-save-draft-button" onClick={handleSaveDraft}>
      Save draft
    </Button>
  )

  const renderBasicsStep = () => (
    <Card className="qa-copilot-guided-panel" data-testid="guided-step-welcome">
      <div className="field-stack">
        {isBaltoAdmin && (
          <FormField required data-testid="guided-organization-field">
            <label>Organization</label>
            <div data-testid="guided-organization-select">
              <Select
                options={organizationOptions}
                value={organizationId}
                onChange={(option) => {
                  const nextOrganizationId = option?.value || ''
                  if (nextOrganizationId !== organizationId) {
                    updateEligibility(ORG_SCOPED_ELIGIBILITY_FIELDS)
                    if (isEditing) {
                      clearLoadedScorecard()
                    }
                  }
                  setOrganizationId(nextOrganizationId)
                  onOrganizationChange(nextOrganizationId)
                }}
                placeholder="Select Organization"
              />
            </div>
          </FormField>
        )}

        <SegmentedControl
          fullWidth
          aria-label="Setup mode"
          items={BUILDER_MODES.map((mode) => ({
            accessor: mode.key,
            label: mode.label,
            icon: mode.icon,
            active: isEditing === (mode.key === 'edit'),
            disabled: isSubmitting,
            onClick: () => {
              if (hasUnsavedProgress) {
                saveGuidedDraft(currentUserId, draftBody)
              }
              onModeChange(mode.key === 'edit')
            },
            dataTestId: `guided-mode-${mode.key}`,
          }))}
        />

        {isEditing && (
          <FormField required data-testid="guided-scorecard-field">
            <label>Scorecard</label>
            <div data-testid="guided-scorecard-select">
              <Select
                options={scorecardOptions}
                value={existingConfig?.id ?? ''}
                loading={scorecardOptionsLoading || isLoadingScorecard}
                isDisabled={!organizationId}
                onChange={handleSelectScorecard}
                placeholder={
                  organizationId ? 'Select a scorecard to edit' : 'Choose an organization first'
                }
              />
            </div>
          </FormField>
        )}

        {!isEditing && (
          <FormField required data-testid="guided-scorecard-name-field">
            <label htmlFor="guided-scorecard-name">Scorecard Name</label>
            <TextInput
              id="guided-scorecard-name"
              type="text"
              value={scorecardName}
              onChange={(event) => setScorecardName(event.target.value)}
              placeholder="e.g. Harborview Claims QA"
              data-testid="guided-scorecard-name"
            />
          </FormField>
        )}
      </div>

      {!isEmpty(validationError) && <ErrorMessage content={validationError} />}
    </Card>
  )

  const renderEligibilityStep = () => (
    <GuidedEligibilityStep
      eligibility={eligibility}
      updateEligibility={updateEligibility}
      eligibilityOptions={eligibilityOptions}
      eligibilityLoading={eligibilityLoading}
      editable={isEditing}
      editingNotice={
        isEditing ? (
          isNil(existingConfig?.eligibility_prompt_id) ? (
            'This scorecard has no eligibility rules yet. Answer either question to add some, or leave both blank'
          ) : (
            <>
              Leave both boxes blank to keep the current eligibility rules —{' '}
              <strong>any changes here will replace the original prompt</strong>
            </>
          )
        ) : null
      }
      lockedUntilEdit={
        isEditing && (isNil(loadedState) || isEqual(eligibility, loadedState.eligibility))
      }
      originalEligibility={loadedState?.eligibility}
      validationError={validationError}
    />
  )

  const renderCustomStep = () => (
    <GuidedStructureStep
      sections={sections}
      criteriaRows={criteriaRows}
      selectedCriteriaRowId={selectedCriteriaRowId}
      onCriteriaSelect={setSelectedCriteriaRowId}
      onAddCriterionToSection={handleAddCriterionToSection}
      onRemoveCriterion={handleRemoveCriterion}
      onReplaceCriterion={handleReplaceCriterion}
      onUpdateSelectedCriteria={handleUpdateSelectedCriteria}
      onAddSection={handleAddSection}
      onUpdateSection={handleUpdateSectionName}
      onRemoveSection={handleRemoveSection}
      validationError={validationError}
    />
  )

  const renderReviewStep = () => {
    const activeFilters = [
      eligibility.minDurationMinutes &&
        `Call length at least ${eligibility.minDurationMinutes} min`,
      eligibility.maxDurationMinutes && `Call length at most ${eligibility.maxDurationMinutes} min`,
      !isEmpty(eligibility.agents) && `${eligibility.agents.length} agents`,
      !isEmpty(eligibility.tags) && `${eligibility.tags.length} tags`,
      !isEmpty(eligibility.playbooks) && `${eligibility.playbooks.length} playbooks`,
      !isEmpty(eligibility.voipCampaigns) && `${eligibility.voipCampaigns.length} VoIP campaigns`,
    ].filter(Boolean)

    const totalPoints = criteriaRows.reduce(
      (runningTotal, criteriaRow) =>
        runningTotal + parsePositiveInteger(criteriaRow.points, DEFAULT_GUIDED_POINTS),
      0
    )

    const summaryRows = [
      { label: 'Name', value: scorecardName },
      {
        label: 'Organization',
        value:
          organizationOptions.find(
            (organizationOption) => organizationOption.value === organizationId
          )?.label || organizationId,
      },
      { label: 'Sections', value: <Pill small>{sections.length}</Pill> },
      { label: 'Criteria', value: <Pill small>{criteriaRows.length}</Pill> },
      {
        label: 'Total points',
        value: (
          <Pill small informative bold>
            {totalPoints}
          </Pill>
        ),
      },
    ]

    if (isEditing) {
      const changes = isNil(loadedState) ? [] : describeChanges(currentState, loadedState)

      return (
        <div className="flex-column gap" data-testid="guided-step-review">
          <Card
            compact
            className="qa-copilot-guided-panel"
            title="Changes"
            subtitle={
              isEmpty(changes)
                ? 'Nothing has changed yet, so there is nothing to save'
                : 'Only what is listed here is written back; everything else stays as it is'
            }
            data-testid="guided-review-changes"
          >
            {!isEmpty(changes) && (
              <Table compact>
                <thead>
                  <tr>
                    <th>Change</th>
                    <th>Where</th>
                    <th>What</th>
                    <th>Detail</th>
                  </tr>
                </thead>
                <tbody>
                  {changes.map((change) => (
                    <tr key={`${change.where}-${change.item}-${change.detail}`}>
                      <td className="collapsing">
                        <Pill
                          small
                          success={change.kind === 'added'}
                          critical={change.kind === 'removed'}
                          informative={change.kind === 'changed'}
                        >
                          {change.kind}
                        </Pill>
                      </td>
                      <td className="muted-text">{change.where}</td>
                      <td className="bold force-wrap">{change.item}</td>
                      <td className="force-wrap">{change.detail}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card>

          {!isEmpty(submitError) && <ErrorMessage content={submitError} />}
        </div>
      )
    }

    return (
      <div className="flex-column gap" data-testid="guided-step-review">
        <div className="equal-grid">
          <Card compact className="qa-copilot-guided-panel" title="Scorecard">
            <Table plain>
              <tbody>
                {summaryRows.map((summaryRow) => (
                  <tr key={summaryRow.label}>
                    <td className="muted-text">{summaryRow.label}</td>
                    <td className="right-aligned force-wrap">{summaryRow.value}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card>

          <Card compact className="qa-copilot-guided-panel" title="Eligibility">
            <div className="flex-column gap">
              <div>
                <p className="qa-copilot-guided-eyebrow uppercase muted-text bold">Review rules</p>
                <p className="no-margin pre-wrap">
                  {eligibility.reviewRules.trim() || 'Every call is eligible.'}
                </p>
              </div>
              <div>
                <p className="qa-copilot-guided-eyebrow uppercase muted-text bold">Exclusions</p>
                <p className="no-margin pre-wrap">
                  {eligibility.exclusions.trim() || 'No calls are excluded.'}
                </p>
              </div>
              <div>
                <p className="qa-copilot-guided-eyebrow uppercase muted-text bold">Filters</p>
                {isEmpty(activeFilters) ? (
                  <p className="no-margin">No filters applied.</p>
                ) : (
                  <div className="flex-wrap small-gap">
                    {activeFilters.map((activeFilter) => (
                      <Pill key={activeFilter} small informative>
                        {activeFilter}
                      </Pill>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </Card>
        </div>

        <Card
          compact
          className="qa-copilot-guided-panel"
          title="Structure"
          subtitle="Open a section to see the criteria in it."
        >
          <GuidedReviewStructure sections={sections} criteriaRows={criteriaRows} />
        </Card>

        {!isEmpty(submitError) && <ErrorMessage content={submitError} />}
      </div>
    )
  }

  const renderStepFooter = () => {
    const footerLayout = (leftButton, rightButton) => (
      <div className="flex-align-center flex-space-between gap full-width">
        <div>{leftButton}</div>
        <div className="flex-align-center small-gap">
          {draftButtons}
          {rightButton}
        </div>
      </div>
    )

    const backButton = (targetStep) => (
      <Button onClick={() => setCurrentStep(targetStep)} disabled={isSubmitting}>
        Back
      </Button>
    )

    switch (currentStep) {
      case GUIDED_STEPS.welcome:
        return footerLayout(
          null,
          <Button
            brand
            data-testid="guided-next-eligibility-button"
            onClick={handleMoveToEligibilityStep}
          >
            Next
          </Button>
        )
      case GUIDED_STEPS.eligibility:
        return footerLayout(
          backButton(GUIDED_STEPS.welcome),
          <Button brand data-testid="guided-next-custom-button" onClick={handleMoveToCustomStep}>
            Next
          </Button>
        )
      case GUIDED_STEPS.custom:
        return footerLayout(
          backButton(GUIDED_STEPS.eligibility),
          <Button
            brand
            data-testid="guided-next-review-button"
            onClick={handleMoveToReviewStep}
            disabled={!isCustomStepValid}
            disabledTooltip={structureBlockedReason()}
          >
            Next
          </Button>
        )
      case GUIDED_STEPS.review:
        return footerLayout(
          backButton(GUIDED_STEPS.custom),
          <Button
            brand
            data-testid="guided-submit-button"
            onClick={handleSubmitGuidedSetup}
            loading={isSubmitting}
            disabled={isSubmitting || !isReviewReadyForSubmit}
          >
            {isEditing ? <IconDeviceFloppy /> : <IconSparkles />}
            {isEditing ? 'Save Changes' : 'Create Scorecard'}
          </Button>
        )
      default:
        return null
    }
  }

  const renderUnsavedChangesModal = () => (
    <BasicModal
      title="Unsaved changes"
      onClose={() => setShowUnsavedChangesModal(false)}
      show={showUnsavedChangesModal}
      size="tiny"
      canCloseOnDimmerClick={false}
      footer={
        <>
          <Button onClick={handleDiscardChanges} data-testid="guided-discard-changes-button">
            Discard changes
          </Button>
          <Button
            brand
            onClick={handleSaveDraftAndClose}
            data-testid="guided-save-draft-close-button"
          >
            Save draft
          </Button>
        </>
      }
    >
      <div data-testid="guided-unsaved-changes-modal">
        You have unsaved changes that will be lost
      </div>
    </BasicModal>
  )

  const stepRenderers = {
    [GUIDED_STEPS.welcome]: renderBasicsStep,
    [GUIDED_STEPS.eligibility]: renderEligibilityStep,
    [GUIDED_STEPS.custom]: renderCustomStep,
    [GUIDED_STEPS.review]: renderReviewStep,
  }

  const currentStepIndex = Math.max(STEP_ORDER.indexOf(currentStep), 0)
  const currentStepDefinition = stepDefinitions[currentStepIndex]
  // Only steps already walked through are marked done: a step ahead of the cursor has not been
  // seen, and the wizard is linear, so index order is the whole of "completed".
  const completedSteps = STEP_ORDER.slice(0, currentStepIndex)

  return (
    <BasicModal
      show
      onClose={handleRequestClose}
      title={isEditing ? 'Edit QA Scorecard' : modalTitle}
      size="large"
      scrolling
      className="qa-copilot-guided-modal"
      data-testid="qa-copilot-guided-setup-modal"
      footer={renderStepFooter()}
    >
      <div className="qa-copilot-guided-layout" data-testid="qa-copilot-guided-flow">
        <GuidedStepNav
          steps={stepDefinitions}
          currentStep={currentStep}
          completedSteps={completedSteps}
          onStepSelect={setCurrentStep}
        />

        <section className="qa-copilot-guided-content flex-column gap">
          <GuidedStepHeader
            stepNumber={currentStepIndex + 1}
            totalSteps={STEP_ORDER.length}
            title={currentStepDefinition.title}
            description={currentStepDefinition.description}
          />
          {stepRenderers[currentStep]?.()}
        </section>
      </div>
      {showUnsavedChangesModal && renderUnsavedChangesModal()}
    </BasicModal>
  )
}

export const QACopilotGuidedSetupModal = () => {
  const dispatch = useDispatch()
  const history = useHistory()
  const { organizations } = useSelector((state) => state)
  const {
    organizationid: currentUserOrganizationId,
    user_id: currentUserId,
    prompt_editor: isPromptEditor,
  } = useSelector((state) => state.currentUser)
  const {
    data: { agents = [], tags = [], playbooks = [], allVoipCampaigns = [] },
    loading: qaCopilotLoading,
  } = useSelector((state) => state.qaCopilot)
  const {
    modelOptions,
    loading: loadingPrompt,
    prompts: existingPrompts = [],
  } = useSelector((state) => state.prompts)
  const canEditPrompts = currentUserOrganizationId === 1 || Boolean(isPromptEditor)
  const [scorecardConfigs, setScorecardConfigs] = useState([])
  const [loadingScorecards, setLoadingScorecards] = useState(false)
  const [isEditing, setIsEditing] = useState(() => loadGuidedDraft(currentUserId)?.mode === 'edit')
  const scorecardsRequestRef = useRef(0)
  const [organizationId, setOrganizationId] = useState('')

  const organizationOptions = useMemo(() => getOrganizationOptions(organizations), [organizations])
  const scorecardOptions = useMemo(
    () =>
      scorecardConfigs.map((config) => {
        const criteriaCount = (config.sections || [])
          .flatMap((section) => section.measures || [])
          .flatMap((measure) => measure.criteria || []).length

        return {
          value: config.id,
          label: config.name,
          description: isArray(config.sections)
            ? `${pluralize(config.sections.length, 'section', 'sections')} · ${pluralize(criteriaCount, 'criterion', 'criteria')}`
            : undefined,
        }
      }),
    [scorecardConfigs]
  )
  const qaCopilotModelOptions = modelOptions?.qa_copilot || []
  const fallbackModelOptions = modelOptions?.copilot_fallback || []

  const defaultQaCopilotModelId = useMemo(
    () =>
      getDefaultQaCopilotModelId(qaCopilotModelOptions) ||
      getDefaultFallbackModelId(fallbackModelOptions),
    [qaCopilotModelOptions, fallbackModelOptions]
  )

  useEffect(() => {
    dispatch(fetchOrganizations())
    dispatch(fetchModels())
  }, [dispatch])

  const handleClose = () => dispatch(closeModal())

  const handleOrganizationChange = (nextOrganizationId) => {
    setOrganizationId(nextOrganizationId)
    if (!nextOrganizationId) {
      return
    }
    dispatch(fetchAgentsByOrg(nextOrganizationId))
    dispatch(fetchTagsByOrg(nextOrganizationId))
    dispatch(fetchPlaybooksByOrg(nextOrganizationId))
    dispatch(fetchOrganizationsMappedVoipCampaigns(nextOrganizationId))
    const requestId = ++scorecardsRequestRef.current
    setLoadingScorecards(true)
    fetchingAPI(
      `${apiService.scorecard}/scoring/scorecards/current?requested_organization_id=${nextOrganizationId}&scorecard_type=copilot`,
      'GET',
      dispatch
    )
      .then((configs) => {
        if (requestId === scorecardsRequestRef.current) {
          setScorecardConfigs(sortBy(configs, 'name'))
        }
      })
      .catch((error) => {
        console.error('Guided builder scorecard list failed', error)
        toast.error(apiErrorMessage(error, 'Failed to fetch scorecards'))
      })
      .finally(() => {
        if (requestId === scorecardsRequestRef.current) {
          setLoadingScorecards(false)
        }
      })
  }

  const handleLoadScorecard = (scorecardConfigId) =>
    dispatch(fetchScorecardConfigById(scorecardConfigId, canEditPrompts))

  const buildScorecardConfig = async ({
    scorecardName,
    organizationId,
    eligibilityReviewRules,
    eligibilityExclusions,
    eligibility,
    loadedEligibility,
    sections,
    criteriaRows,
    existingConfig,
  }) => {
    // Every prompt this builds is stamped with `defaultQaCopilotModelId`, and one
    // with no model has nothing for the API to run on. Checked before the prompt
    // generation below, which is several LLM round-trips we'd only throw away.
    if (!defaultQaCopilotModelId) {
      throw new Error('No AI model is available yet. Wait for models to load and try again.')
    }

    const generateCriteriaPrompt = async ({
      description,
      passScenario,
      failScenario,
      notApplicableScenario,
    }) => {
      const context = [
        `When this should pass: ${passScenario || ''}`.trim(),
        `When this should fail: ${failScenario || ''}`.trim(),
        `When this criterion is not applicable: ${notApplicableScenario || ''}`.trim(),
      ].join('\n')
      const promptResult = await dispatch(
        generateCopilotPrompt(
          'qa',
          description,
          context,
          `Criterion should pass when: ${passScenario || ''}`,
          `Criterion is not applicable when: ${notApplicableScenario || ''}`,
          `Criterion should fail when: ${failScenario || ''}`
        )
      )
      if (isEmpty(promptResult?.generated_prompt)) {
        throw new Error('Failed to generate one or more criterion prompts.')
      }
      return ensureTranscriptPlaceholder(promptResult.generated_prompt)
    }

    const existingPromptsByScid = existingConfig ? keyBy(existingPrompts, 'scid') : {}
    const { sections: builtSections, promptsToAdd } = await buildGuidedSectionsAndPrompts({
      sections,
      criteriaRows,
      existingSections: existingConfig?.sections,
      existingPromptsByScid,
      generatePrompt: generateCriteriaPrompt,
      defaultQaCopilotModelId,
    })

    // Both eligibility fields are optional now, and with neither of them there is nothing to
    // describe: generating a prompt from two empty strings would hand the model an instruction
    // with no rule in it, so the scorecard is created without an eligibility prompt instead.
    const hasEligibilityRules =
      !isEmpty(eligibilityReviewRules.trim()) || !isEmpty(eligibilityExclusions.trim())
    let eligibilityPrompt = null

    if (hasEligibilityRules) {
      const eligibilityContext = [
        `Review rules: ${eligibilityReviewRules}`,
        `Exclusions: ${eligibilityExclusions}`,
      ].join('\n')

      const eligibilityPromptResult = await dispatch(
        generateCopilotPrompt(
          'eligibility',
          'Determine whether this call should be prioritized for QA review.',
          eligibilityContext,
          `Calls are eligible when: ${eligibilityReviewRules}`,
          `Calls are ineligible when: ${eligibilityExclusions}`,
          ''
        )
      )
      if (isEmpty(eligibilityPromptResult?.generated_prompt)) {
        throw new Error('Failed to generate eligibility prompt.')
      }
      eligibilityPrompt = ensureTranscriptPlaceholder(eligibilityPromptResult.generated_prompt)
    }

    return {
      scorecardConfig: {
        ...(existingConfig ?? {
          created_via: 'guided_builder',
          active: false,
          should_eligibility_respond_yes: true,
          required_score_count: 10,
        }),
        name: scorecardName,
        organization_id: Number(organizationId),
        sections: builtSections,
        type: 'copilot',
        ...eligibilityFilterOverrides(eligibility, loadedEligibility),
      },
      prompts: [
        ...promptsToAdd,
        ...(eligibilityPrompt
          ? [
              {
                ...existingPromptsByScid.eligibility,
                scid: 'eligibility',
                user_prompt: eligibilityPrompt,
                side: 'both',
                llm_model_id:
                  existingPromptsByScid.eligibility?.llm_model_id ?? defaultQaCopilotModelId,
                feature: 'qa_copilot_eligibility',
              },
            ]
          : []),
      ],
    }
  }

  const handleCreateScorecard = async (payload) => {
    const { scorecardConfig, prompts } = await buildScorecardConfig(payload)
    const createdScorecard = await dispatch(
      createScorecardConfig(scorecardConfig, history, prompts)
    )

    if (!createdScorecard) {
      throw new Error('Failed to create scorecard.')
    }
    handleClose()
  }

  const handleUpdateScorecard = async (payload) => {
    const { scorecardConfig, prompts } = await buildScorecardConfig(payload)
    const updatedScorecard = await dispatch(
      updateScorecardConfig(scorecardConfig, history, prompts)
    )

    if (!updatedScorecard) {
      throw new Error('Failed to save changes')
    }
    handleClose()
  }

  const isLoadingModels =
    loadingPrompt.modelOptions && isEmpty(qaCopilotModelOptions) && isEmpty(fallbackModelOptions)

  if (isLoadingModels) {
    return (
      <BasicModal
        show
        onClose={handleClose}
        title="Configure QA Scorecard"
        size="large"
        data-testid="qa-copilot-guided-setup-modal"
      >
        <div className="flex-center padding-large" data-testid="qa-copilot-guided-setup-loading">
          <Loader />
        </div>
      </BasicModal>
    )
  }

  return (
    <GuidedCreateFlow
      key={isEditing ? 'edit' : 'create'}
      isEditing={isEditing}
      initialOrganizationId={organizationId}
      onModeChange={setIsEditing}
      scorecardOptions={scorecardOptions}
      scorecardOptionsLoading={loadingScorecards}
      onLoadScorecard={handleLoadScorecard}
      onUpdateScorecard={handleUpdateScorecard}
      organizationOptions={organizationOptions}
      currentUserOrganizationId={currentUserOrganizationId}
      currentUserId={currentUserId}
      eligibilityOptions={{
        agents,
        tags,
        playbooks,
        voipCampaigns: allVoipCampaigns.map((campaign) => ({
          value: campaign.id,
          label: campaign.name,
        })),
      }}
      eligibilityLoading={{
        agents: qaCopilotLoading.agents,
        tags: qaCopilotLoading.tags,
        playbooks: qaCopilotLoading.playbooks,
        voipCampaigns: qaCopilotLoading.allVoipCampaigns,
      }}
      onOrganizationChange={handleOrganizationChange}
      onCreateScorecard={handleCreateScorecard}
      onClose={handleClose}
      modalTitle="Configure QA Scorecard"
    />
  )
}

export default QACopilotGuidedSetupModal
